import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TriggerService, TriggerDef } from '../../services/trigger.service';
import { WhatsAppTriggerService } from '../../services/whatsapp-trigger.service';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html'
})
export class FooterComponent implements OnInit, OnDestroy {
  subscribeForm: FormGroup;
  subscribeSuccess = false;
  subscribeError = false;
  showScrollTop = false;

  /** Active trigger from the trigger service — drives nudge label + pulse */
  activeTrigger: TriggerDef | null = null;
  private sub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private triggerService: TriggerService,
    private whatsapp: WhatsAppTriggerService,
    private dataLayer: DataLayerService,
  ) {
    this.subscribeForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnInit() {
    this.sub = this.triggerService.current$.subscribe(t => this.activeTrigger = t);
    // clicked$ fires when interceptor stops propagation (trigger active) — Angular's
    // (click) handler is killed by stopImmediatePropagation in that case, so we
    // push the dataLayer event here instead.
    this.sub.add(this.whatsapp.clicked$.subscribe(() => this.onWhatsAppClick()));
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  @HostListener('window:scroll')
  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    this.showScrollTop = scrollTop > docHeight * 0.7;
  }

  subscribe() {
    if (this.subscribeForm.invalid) return;
    this.subscribeSuccess = true;
    this.subscribeError = false;
    this.subscribeForm.reset();
    setTimeout(() => this.subscribeSuccess = false, 5000);
  }

  /**
   * WhatsApp URL — dynamic per active trigger.
   * If a trigger is firing, use its auto-message (e.g. T1 wall-size, T2 surface...).
   * Otherwise fall back to a short friendly default ("Hi! How can you help me?").
   */
  get whatsappUrl(): string {
    return this.whatsapp.buildWhatsAppUrl(this.activeTrigger?.autoMessage);
  }

  /** Tooltip text — shows trigger context on hover */
  get whatsappTooltip(): string {
    return this.activeTrigger
      ? `${this.activeTrigger.id}: ${this.activeTrigger.name} — Tap to chat`
      : 'Need help? Chat with us on WhatsApp';
  }

  dismissNudge() {
    this.triggerService.dismiss();
  }

  onWhatsAppClick() {
    this.dataLayer.pushWhatsAppClick(this.activeTrigger);
    // When no trigger is active, interceptor doesn't run — open WhatsApp manually.
    // When trigger IS active, interceptor handles window.open via clicked$ path.
    if (!this.activeTrigger) {
      window.open(this.whatsappUrl, '_blank', 'noopener');
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
